import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { exploreAPI } from '../../api/endpoints';
import { formatXPUNT } from '../../utils/helpers';
import { FaUser, FaStar, FaUsers, FaLock, FaCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './ExploreCreators.css';

const ExploreCreators = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creators, setCreators] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState({});

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    setLoading(true);
    try {
      const response = await exploreAPI.getCreators();
      setCreators(response.data || []);
      
      // Check subscription status for each creator if authenticated
      if (isAuthenticated) {
        const statuses = {};
        for (const creator of response.data) {
          try {
            const statusRes = await exploreAPI.getSubscriptionStatus(creator.id);
            statuses[creator.id] = statusRes.data;
          } catch (e) {
            // Skip
          }
        }
        setSubscriptionStatus(statuses);
      }
    } catch (error) {
      console.error('Failed to fetch creators:', error);
      toast.error('Failed to load creators');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (creatorId) => {
    if (!isAuthenticated) {
      toast.error('Please login to subscribe');
      return;
    }

    try {
      // This would call the subscribe API
      toast.success('Subscribed successfully!');
      fetchCreators();
    } catch (error) {
      toast.error('Failed to subscribe');
    }
  };

  if (loading) {
    return (
      <div className="explore-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="explore-creators">
      <div className="page-header">
        <h1>Explore Creators</h1>
        <p className="page-subtitle">
          Subscribe to top creators and get access to exclusive premium bet codes
        </p>
      </div>

      {creators.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No creators found</h3>
          <p>Be the first to become a creator!</p>
        </div>
      ) : (
        <div className="creators-grid">
          {creators.map((creator) => {
            const isSubscribed = subscriptionStatus[creator.id]?.is_subscribed || false;
            const isNew = creator.is_new;

            return (
              <div key={creator.id} className="creator-card">
                <div className="creator-card-header">
                  <div className="creator-avatar-wrapper">
                    <img 
                      src={creator.avatar || '/default-avatar.png'} 
                      alt={creator.display_name}
                      className="creator-avatar"
                    />
                    {creator.is_verified && (
                      <span className="verified-badge">✓</span>
                    )}
                    {isNew && (
                      <span className="new-badge">New</span>
                    )}
                  </div>
                  <div className="creator-info">
                    <h3 className="creator-name">
                      {creator.display_name}
                      {creator.is_verified && (
                        <FaCheck className="verified-icon" />
                      )}
                    </h3>
                    <p className="creator-bio">{creator.bio || 'Professional bettor'}</p>
                  </div>
                </div>

                <div className="creator-stats">
                  <div className="stat">
                    <FaUsers className="stat-icon" />
                    <span>{creator.total_subscribers || 0} subscribers</span>
                  </div>
                  <div className="stat">
                    <FaLock className="stat-icon" />
                    <span>{creator.private_bet_count || 0} private bets</span>
                  </div>
                  <div className="stat">
                    <FaStar className="stat-icon" />
                    <span>{formatXPUNT(creator.subscription_price)} / {creator.subscription_type}</span>
                  </div>
                </div>

                <div className="creator-actions">
                  {isSubscribed ? (
                    <div className="subscribed-badge">
                      <FaCheck /> Subscribed
                    </div>
                  ) : (
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleSubscribe(creator.id)}
                    >
                      Subscribe {formatXPUNT(creator.subscription_price)}
                    </button>
                  )}
                  <Link 
                    to={`/explore/creator/${creator.id}`}
                    className="btn btn-secondary"
                  >
                    View Profile
                  </Link>
                </div>

                <div className="creator-card-footer">
                  <div className="creator-tags">
                    <span className="tag">Premium</span>
                    <span className="tag">Exclusive</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExploreCreators;